import { zodResolver } from "@hookform/resolvers/zod";
import { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import { useNavigate } from "@tanstack/react-router";
import { useResetPasswordMutation } from "../api/mutations";
import { resetPasswordSchema } from "../schemas";
import { ResetPasswordType, ResetSuccessMessage, ResetErrorMessage } from "../types";

type ResetResponse = ResetSuccessMessage | ResetErrorMessage;

function isSuccessResponse(response: ResetResponse): response is ResetSuccessMessage {
  return 'status' in response && 'message' in response;
}

function isErrorResponse(response: ResetResponse): response is ResetErrorMessage {
  return 'details' in response && response.details?.message !== undefined;
}

export const useReset = () => {
    const [error, setError] = useState<string | null>(null);
    const navigate = useNavigate();
    const { mutateAsync: resetPassword, isPending } = useResetPasswordMutation();
    
    const form = useForm<ResetPasswordType>({
      defaultValues: { email: "" },
      resolver: zodResolver(resetPasswordSchema),
    });

    const clearError = () => setError(null);

    // Subscribe to form changes to clear errors
    useEffect(() => {
      const subscription = form.watch(() => {
        if (error) {
          clearError();
        }
      });
      return () => subscription.unsubscribe();
    }, [error, form]);
    
    const onSubmit = async (data: ResetPasswordType) => {
      setError(null);
      
      try {
        const response = await resetPassword(data) as ResetResponse;
        
        // Check for success response
        if (isSuccessResponse(response)) {
          toast.success(response.message);
          navigate({ to: '/success/$type', params: { type: 'reset' } });
          return;
        }
        
        // Handle error response
        let errorMessage = "Failed to reset password";
        
        if (isErrorResponse(response)) {
          errorMessage = response.details.message;
        }
        
        // Transform error message if it matches
        if (errorMessage.toLowerCase().includes("invalid email")) {
          errorMessage = "Email doesn't exist...";
        }
        
        setError(errorMessage);
        toast.error(errorMessage, {
          description: "Please check your email and try again."
        });
      } catch (err) {
        // console.log(err);
        const errorMessage = "An unexpected error occurred";
        setError(errorMessage);
        toast.error(errorMessage);
      }
    };
    
    return { form, onSubmit, isLoading: isPending, error, clearError };
  };